import { useEffect, useMemo, useRef, useState } from "react";
import { Typography, Box, Stack, IconButton, InputBase, Menu, MenuItem, ButtonBase, useTheme } from "@mui/material";
import { ZoomIn, ZoomOut, ChevronDown, Check } from "lucide-react";
import { MENU_PAPER_SX, menuPaperChromeSx } from "@repo/ui";
import { ITheme } from "store/theme/types";
import { STUDIO_PREVIEW_PRODUCT_MESSAGE, type StudioPreviewProduct } from "@repo/types";
import { DeviceKey, DEVICE_FRAMES } from "./previewConfig";
import { vhMinus } from 'utils/textZoom'

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.25;

/**
 * Module scope, not a closure, so the `zoom` state INITIALISER can use it — a container
 * passing `initialZoom` must land inside the same bounds the +/- buttons enforce, rather
 * than being the one value in the system that skips the clamp.
 */
const clampZoom = (z: number) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));

/**
 * What "100%" means on screen, per container.
 *
 * Studio previews inside a generous two-pane split, where the raw frame was too small to
 * read, so 100% there renders at 1.35x. The certificate drawer is 500px wide and even 1.5x
 * overflowed it — the phone came out larger than the space holding it. So this is a PROP
 * with a Studio-shaped default rather than a global constant: the baseline belongs to the
 * container, not to the preview.
 *
 * `zoom` stays the USER's number (1 = 100%) in both, so the label, the clamp and the step
 * arithmetic never have to know which container they are in.
 *
 * 1.35, DOWN FROM 1.5 (VRG-221). At 1.5 the mobile frame rendered 390x844 — its native
 * viewport, 1:1 — and 844 is taller than the pane can show on most windows, so the device
 * arrived cropped top and bottom with its bezel cut off. MEASURED at 1280x1200, the case
 * where the two zooms actually differ in kind:
 *
 *   zoom 100% (1.5 base)  content 904px into an 869px slot  -> slack -35, bezel clipped 1.5px
 *   zoom  90% (1.35)      content 820px into an 869px slot  -> slack +49, whole device visible
 *
 * So the old 100% was never a usable resting state and users were wheel-zooming to 90% to
 * get one. 1.35 makes that resting state the default. It is a change to the BASELINE only:
 * ZOOM_MIN/MAX/STEP are untouched and both bounds stay useful (0.5 -> 0.675x renders a
 * 263x570 phone, 2 -> 2.7x a 702x1520 one).
 */
// SUPERSEDED as a shared default — the per-device values now live in `DEVICE_FRAMES`
// (previewConfig), because 1.35 was tuned on the mobile frame and produced a ~1026px
// desktop into a ~640px pane. Kept only as the mobile figure's provenance; the `baseZoom`
// PROP still overrides everything for containers that need it (the certificate drawer).
const DEFAULT_BASE_ZOOM = 1.35;
void DEFAULT_BASE_ZOOM;

/**
 * Height of the preview pane, as the px offset `vhMinus` subtracts from the viewport.
 *
 * Belongs to the CONTAINER for the same reason `baseZoom` does. Studio's pane runs from
 * under the tab row to the page's 24px bottom gutter. `vh - 186.5`: the pane starts 16px
 * below the chrome block (see `STUDIO_PANE_TOP_GAP`), so that gap comes out of its height.
 *
 * The .5, and why it is not 185: pinned, the pane's top is at 162.5 (138.5 + the
 * scrollport's 24px padding edge), so at `vh - 185` its BOTTOM lands at vh - 22.5 — 1.5px
 * past the page's 24px bottom gutter. A sticky element cannot overhang its containing
 * block, so at the very bottom of the scroll it got nudged up by exactly that 1.5px, which
 * reads as the preview twitching as you reach the end of the page. 186.5 lands it on the
 * gutter and it holds still. It was `vh - 249` while a 76px save bar sat under it (VRG-221 moved those
 * buttons into the page header), and that 64px is now the pane's.
 *
 * ⚠️ This offset — and `stickyTop`/`topGap` with it — is STUDIO's geometry, and it is only
 * meaningful for a pane inside a scrolling page. It used to be aimed at the certificate
 * drawer too (`heightOffset: 249`, `stickyTop: 60`), which was the wrong tool: those numbers
 * restate, in viewport terms, a box the drawer's own flex body already measured. The two
 * disagreed by a constant ~108px — 60px dead above the pane, 48px of slack below it, at
 * EVERY viewport height, since both terms scale with `vh` and the error does not. A
 * container that bounds the pane itself now passes `fillContainer` instead; see that prop.
 */
const STUDIO_PANE_OFFSET = 186.5;

/**
 * Where the pane pins, as a sticky `top` measured from the scrollport's PADDING edge (the app
 * scrolls inside `<main>`, whose padding edge sits 24px down).
 *
 * 138.5 pins the pane at viewport 162.5, which must equal its RESTING position. THAT EQUALITY
 * IS THE WHOLE POINT: differ by even a few px and the pane visibly jumps the moment it pins,
 * which is exactly what "pure sticky" rules out.
 *
 * The chrome block's bottom is now 146.5 (Studio passes `bottomGap={0}` — the 16px band under
 * the tab row read as a black bar across the page). The gap did not disappear, it MOVED: the
 * editor column now scrolls flush to the tab border, while the preview keeps 16px of air above
 * it. So the pane rests 16px BELOW the block rather than against it, via `STUDIO_PANE_TOP_GAP`
 * — and this number is 146.5 + 16 - 24 (the scrollport's padding edge) = 138.5.
 *
 * `topGap` and this constant are ONE measurement expressed twice: the margin sets where the
 * pane rests, this sets where it pins, and they must agree. Change one and you must change the
 * other, or you reintroduce the jump. Anything smaller tucks it behind that block: at the previous
 * `60` it pinned at viewport 84 (measured) and the block now covers 0..162.5, so the top 78px
 * of the phone would have been hidden once the page scrolled.
 *
 * The .5, and why it is not 137.5: the block's bottom edge includes its 1px border, which is
 * always in the box (transparent at rest, coloured once stuck) so that turning it on cannot
 * nudge anything. 137.5 was measured pinning the pane at 161.5 — 1px UNDER that border.
 */
const STUDIO_PANE_STICKY_TOP = 138.5;

/**
 * Air between the tab row and the top of the preview pane, in px.
 *
 * The 16px band that used to sit under the tab row spanned the FULL page width and read as a
 * black bar. Removing it outright was wrong in the other direction: the editor column
 * genuinely wants to scroll flush to the tab border, but the preview — which never scrolls —
 * wants the breathing room. So the gap is now the PREVIEW's, not the page's.
 *
 * Must stay in lockstep with `STUDIO_PANE_STICKY_TOP`; see the note there.
 */
const STUDIO_PANE_TOP_GAP = 16;

interface PreviewProps {
  formValues: Partial<ITheme>;
  /** Which customer-app flow to render in the iframe (e.g. "certificate"). */
  previewPage: string;
  /** Viewport the preview is framed at. */
  device: DeviceKey;
  /**
   * VERA-644 (TD-066 last-mile): the WhiteLabelCard's live (possibly unsaved)
   * "Hide Powered by Vera" toggle state, pushed to the iframe alongside the
   * theme so the mock certificate's Footer badge updates live.
   */
  hidePoweredBy?: boolean;
  /**
   * Which install location the preview represents. The brand header shows on the hosted
   * Vera address and is hidden on an embed, so this is what makes that difference
   * visible rather than merely described in the Logo card's copy.
   */
  embedded?: boolean;
  /**
   * The brand's own install locations, so the caption names a REAL address rather than
   * an abstract "hosted / embedded" mode. Omitted by other containers (the certificate
   * drawer), which then render the caption alone exactly as before.
   */
  installOptions?: { label: string; embedded: boolean }[];
  installLabel?: string;
  onInstallChange?: (label: string) => void;
  /**
   * Render a REAL certificate rather than the app's mock. Omitted by Studio, which is
   * previewing a theme and wants stable filler; set by the certificate preview page, so a
   * brand can see an actual certificate without activating it (activation is billable).
   */
  /**
   * VRG-827: the brand's own product to render INSIDE the mock certificate.
   *
   * Orthogonal to `certificateId` below, which swaps the whole record for a real one
   * and therefore needs a certificate to exist. Studio has none to point at — a brand
   * tuning its theme has products long before certificates, and activation is billable
   * — so this substitutes only the product and keeps the mock's scaffolding.
   *
   * `null` clears back to the mock, which is also what a brand with no products sends.
   */
  product?: StudioPreviewProduct | null;
  certificateId?: string;
  /**
   * VRG-809 — the company being edited. The iframe loads the BARE app origin
   * (app.vera.inc), which has no subdomain and no embed key, so without this the app
   * falls through its resolution chain to a hardcoded seed-company id and renders a
   * DIFFERENT brand's logo and name while this Studio's theme is postMessage'd over it.
   */
  companyId?: string;
  /** What "100%" renders at. Defaults to Studio's 1.35x; a narrow container passes 1. */
  baseZoom?: number;
  /**
   * Where the user's zoom STARTS, as the user's own number (1 = 100%).
   *
   * Sibling of `baseZoom` and owned by the container for the same reason: `baseZoom` sets
   * what 100% renders at, this sets which percentage the container opens on. Studio opens
   * at its resting 100%; the certificate drawer opens at 150% because that drawer exists to
   * READ the certificate, and its `baseZoom` of 1 renders the phone at 260px — legible as a
   * thumbnail, not as a page. 150% of that is 390x844, the phone's native viewport 1:1.
   *
   * Clamped like every other zoom, so a container cannot open outside ZOOM_MIN/MAX.
   */
  initialZoom?: number;
  /**
   * Pane height as a `vhMinus` offset. Defaults to Studio's; a container whose own body
   * bounds the pane passes its own. See `STUDIO_PANE_OFFSET`.
   */
  heightOffset?: number;
  /**
   * Take the height of the PARENT instead of the viewport, and drop the sticky offset.
   *
   * `heightOffset`/`stickyTop`/`topGap` size the pane against `100vh` because Studio's pane
   * lives in a long scrolling page and has to pin. A container that already bounds the pane
   * — the certificate drawer's flex body — cannot be expressed that way: every px of chrome
   * above it has to be restated as a magic offset, and any disagreement shows up as dead
   * space that no viewport fixes. Measured before this existed: the drawer's pane sat 60px
   * below its container's top and ended 48px above its bottom, at EVERY viewport height,
   * because `stickyTop: 60` and `heightOffset: 249` were describing a box the parent already
   * knew the size of.
   *
   * So this is not a tuning knob, it is the other sizing MODE: the parent is the bound.
   */
  fillContainer?: boolean;
  /**
   * Sticky `top` for the pane. Defaults to Studio's, which clears its pinned header block;
   * a container with no sticky chrome above the pane passes its own. See
   * `STUDIO_PANE_STICKY_TOP`.
   */
  stickyTop?: number;
  /**
   * Air above the pane, in px. Defaults to Studio's 16 (see `STUDIO_PANE_TOP_GAP`).
   * A container whose pane should sit flush passes 0 — the certificate drawer does.
   */
  topGap?: number;
  /**
   * VRG-470 (BEHIND A FLAG): preview-scoped controls, rendered LEFT-aligned in the same
   * floating band as the zoom stepper.
   *
   * These are controls of the zoom stepper's class — they choose what the preview renders
   * and at what device, i.e. viewport parameters — as opposed to the Certificate/Email tabs
   * above, which choose which template you are EDITING. Studio currently puts them at the
   * right of the tab row, absolutely positioned OVER it at `top: 6`, which is itself the
   * evidence they never had a home there.
   *
   * Deliberately shares the zoom stepper's band rather than adding a toolbar row: the device
   * frame is the thing being judged on this page, and a new row above it would push it down.
   * The band is `position: absolute`, so nothing in the frame's own layout moves.
   */
  controls?: React.ReactNode;
  /**
   * VRG-549: the Studio token whose surfaces should be outlined inside the iframe, or
   * null. Pushed down the SAME postMessage bridge as the theme — the transport, the
   * origin scoping and the mount handshake already existed, so this is one more message
   * type on a working channel rather than new plumbing.
   */
  highlightToken?: string | null;
  /**
   * What the app reported it actually outlined, e.g. `[{label: 'Primary buttons', count: 2}]`.
   *
   * MEASURED in the app, not asserted here, and that is deliberate: only the app knows
   * its own DOM, and a count hardcoded in the platform would be a claim nobody re-checks
   * the next time a page changes. See `apps/app/src/pages/StudioPreviewPage/tokenHighlight.ts`.
   *
   * `invisible` rides along: elements the app PROVED the token reaches but could not
   * outline, because they paint nothing on screen right now — the contents of a closed
   * drawer, overwhelmingly. It is what lets the panel distinguish "this control changes
   * nothing here" from "everything it changes here is behind a closed sheet", which on the
   * certificate flow is the difference between a false statement and a true one for
   * `border_radius`.
   */
  onHighlightResult?: (result: {
    parts: { label: string; count: number }[];
    invisible: number;
  }) => void;
}

/**
 * Resolve the customer-app origin the preview iframe loads. Configurable via
 * VITE_PREVIEW_APP_URL so a worktree preview can point at its own app instance
 * instead of the shared dev app (default: localhost:5174 in dev).
 */
const resolvePreviewUrl = (): string => {
  const explicit = import.meta.env.VITE_PREVIEW_APP_URL as string | undefined;
  if (explicit) return explicit;
  if (import.meta.env.VITE_ENVIRONMENT === "development")
    return "http://localhost:5174";
  return "https://app.dev.vera.inc";
};

export const Preview = ({ formValues, previewPage, device, hidePoweredBy = false, embedded = false, installOptions, installLabel, onInstallChange, product = null, certificateId, companyId, baseZoom, initialZoom = 1, fillContainer = false, heightOffset = STUDIO_PANE_OFFSET, stickyTop = STUDIO_PANE_STICKY_TOP, topGap = STUDIO_PANE_TOP_GAP, controls, highlightToken = null, onHighlightResult }: PreviewProps) => {
  const [isLoadingIframe, setIsLoadingIframe] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const theme = useTheme();

  const [zoom, setZoom] = useState(() => clampZoom(initialZoom));

  /**
   * The percentage box's in-flight text, or `null` when it is not being edited.
   *
   * Separate from `zoom` because the two answer different questions: `zoom` is what the
   * preview RENDERS at and must stay a valid number at all times, while this is whatever
   * has been typed so far — including the transiently meaningless ("", "1", "abc"). Driving
   * the input off `zoom` directly would make it impossible to clear the field to retype it,
   * since every keystroke would be re-rounded back from the live value.
   */
  const [zoomDraft, setZoomDraft] = useState<string | null>(null);

  /**
   * "The blur you are about to see was an Escape — discard, do not commit."
   *
   * A ref and not state, because the ORDER is the whole point. Escape clears the draft and
   * blurs the field, but `setZoomDraft(null)` is async while `blur()` is synchronous, so the
   * `onBlur` handler still closes over the OLD draft and commits the very value Escape was
   * cancelling. Measured before this existed: typing 60 over a committed 200 and pressing
   * Escape left the preview at 60%. A ref is written and read within the one event, so it is
   * already true by the time blur asks.
   */
  const zoomEscapedRef = useRef(false);

  /** The one rounding of `zoom`, shared by the field's resting value and its seed on focus. */
  const zoomPercent = Math.round(zoom * 100);

  /**
   * Take the typed text, or put it back.
   *
   * Anything that is not a finite number — empty, "abc", "1e", a stray "%" — REVERTS rather
   * than writing a value, because the alternative is a `NaN` width that blanks the preview
   * with no way back. `Number("")` is 0, not NaN, so the empty case needs its own guard; it
   * would otherwise clamp to ZOOM_MIN and read as "clearing the field zooms out".
   *
   * Clearing the draft is what restores the display: with `zoomDraft` null the field re-derives
   * from `zoom`, so a committed value comes back already clamped and rounded.
   */
  const commitZoomDraft = () => {
    if (zoomDraft === null) return;
    const typed = zoomDraft.trim().replace(/%$/, "");
    const parsed = Number(typed);
    if (typed !== "" && Number.isFinite(parsed)) {
      setZoom(clampZoom(parsed / 100));
    }
    setZoomDraft(null);
  };
  const [installAnchor, setInstallAnchor] = useState<HTMLElement | null>(null);
  const previewUrl = useMemo(() => resolvePreviewUrl(), []);
  const frame = DEVICE_FRAMES[device];
  // `baseZoom` is what "100%" renders at and belongs to the CONTAINER (see the prop);
  // `zoom` is the user's number. Keeping them separate is what lets the label, the clamp
  // and the step arithmetic stay ignorant of which container they are in.
  // The PROP wins when a container passes one (the certificate drawer is 500px wide and
  // needs its own baseline); otherwise the device's own value, so 100% is a resting state
  // where the whole device is visible for all three.
  const effectiveBaseZoom = baseZoom ?? frame.baseZoom;
  const framedWidth = Math.round(frame.width * effectiveBaseZoom * zoom);
  const scale = framedWidth / frame.viewportWidth;
  const framedHeight = Math.round(frame.viewportHeight * scale);

  const sendThemeToIframe = (
    themeValues: Partial<ITheme>,
    hidePoweredByValue: boolean,
    embeddedValue: boolean
  ) => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: "theme",
        theme: themeValues,
        hidePoweredBy: hidePoweredByValue,
        embedded: embeddedValue,
      },
      previewUrl
    );
  };

  // VRG-549: ask the app to outline the surfaces a Studio token governs. `null` clears.
  const sendHighlightToIframe = (token: string | null) => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: "studio-highlight", token },
      previewUrl
    );
  };

  // VRG-827: which product the mock certificate should render. Same channel, same
  // origin scoping, same mount handshake as the theme — one more message type on a
  // working bridge rather than new plumbing (the VRG-549 precedent).
  const sendProductToIframe = (value: StudioPreviewProduct | null) => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: STUDIO_PREVIEW_PRODUCT_MESSAGE, product: value },
      previewUrl
    );
  };

  // Re-push the selected product when it changes, and again when the flow changes —
  // switching flow reloads the iframe, so the app comes back with no product at all
  // and would silently fall back to the mock without this.
  useEffect(() => {
    sendProductToIframe(product);
  }, [product, previewPage]);

  // Re-push the live theme (+ white-label toggle) whenever the form changes,
  // the toggle changes, or the page switches.
  useEffect(() => {
    sendThemeToIframe(formValues, hidePoweredBy, embedded);
  }, [formValues, previewPage, hidePoweredBy, embedded]);

  // Re-push the highlight when the operator opens a different control's panel, and again
  // when the flow changes — the new flow's DOM is a different set of matches, and the
  // app answers with a fresh count either way.
  useEffect(() => {
    sendHighlightToIframe(highlightToken);
  }, [highlightToken, previewPage]);

  // The iframe reloads when the page changes; show the loader until it's ready.
  useEffect(() => {
    setIsLoadingIframe(true);
  }, [previewPage]);

  // Switching device returns the user's zoom to 100%. Each device now has its own
  // baseline sized to fit the pane, so carrying a previous device's zoom across would
  // land on a number that fits neither — leave desktop at 60% to see it whole, switch
  // to mobile, and the phone arrives at 60% of a baseline that was already correct.
  // (This fires on mount too, which is why it must read `initialZoom` rather than 1 — a
  // literal here would silently overwrite the container's opening zoom on the first paint.)
  useEffect(() => {
    setZoom(clampZoom(initialZoom));
  }, [device]);

  // Mouse-wheel zoom over the preview (scroll up = zoom in). Native non-passive
  // listener so we can preventDefault and not scroll the page. It fires only over
  // the padding AROUND the iframe: wheel events over the cross-origin iframe go to
  // the iframe and scroll the certificate inside it. (An earlier version of this
  // comment said the previewed app forwards them back by postMessage. It does not
  // — there is no such sender in `apps/app` and no handler for one below. The note
  // further down, "wheel inside the iframe scrolls the certificate; zoom is wheel
  // in the surrounding pane only", is the accurate one.)
  const zoomAreaRef = useRef<HTMLDivElement>(null);
  /*
   * VRG-720 — zoom proportional to how far the wheel actually moved.
   *
   * This applied a FLAT ±0.1 per wheel EVENT, ignoring `deltaY` entirely beyond
   * its sign. A mouse notch and the lightest trackpad nudge moved the same 10
   * points — but a trackpad fires a burst of events per gesture, commonly 20–40,
   * so one two-finger flick asked for 200–400 points against a range that is only
   * 150 wide (0.5–2). It hit the clamp before the finger left the pad, which is
   * the "too sensitive, scrolls too quick" report.
   *
   * Two changes. The step is now driven by the delta's MAGNITUDE, so a small
   * movement makes a small change. And it is MULTIPLICATIVE (`exp`), so a given
   * gesture changes zoom by the same PROPORTION wherever you are in the range —
   * additive steps feel coarse near 0.5 and sluggish near 2, because 0.1 is a
   * fifth of the former and a twentieth of the latter.
   *
   * At 0.0012/px: a 100px mouse notch is ~12.7% per notch, and a ~4px trackpad
   * event is ~0.5%, so a 30-event flick lands near 15% rather than slamming the
   * clamp. Trackpad pinch arrives as ctrl+wheel with larger deltas and needs no
   * special case — scaling by magnitude already handles it.
   *
   * `deltaMode` is normalised because it is not always pixels: Firefox reports
   * lines (1) for a real mouse wheel, where a raw `deltaY` of 3 would otherwise
   * register as a near-zero nudge.
   */
  const ZOOM_WHEEL_PER_PX = 0.0012;
  const applyWheelZoom = (deltaY: number, deltaMode: number) => {
    const px = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY;
    /*
     * NO rounding here — it goes in the label (`Math.round(zoom * 100)%`) instead,
     * and that separation is load-bearing rather than tidiness. Rounding the STATE
     * to 2dp makes every step smaller than half a percent a no-op: 1 * exp(0.0048)
     * is 1.0048, which rounds straight back to 1.00, so the next event starts from
     * 1.00 again and a trackpad flick accumulates exactly nothing. Measured that
     * way round first — 30 synthetic 4px events moved the label 100% → 100% — so
     * the fix for an over-sensitive control had quietly made it inert below a
     * threshold while still jumping on a mouse notch. Keep the precision in state;
     * round only what is displayed.
     */
    setZoom((z) => clampZoom(z * Math.exp(-px * ZOOM_WHEEL_PER_PX)));
  };
  useEffect(() => {
    const el = zoomAreaRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      applyWheelZoom(e.deltaY, e.deltaMode);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Latest form values / toggle, readable from the message listener without re-binding it.
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;
  const hidePoweredByRef = useRef(hidePoweredBy);
  hidePoweredByRef.current = hidePoweredBy;
  const embeddedRef = useRef(embedded);
  embeddedRef.current = embedded;
  // Defensive re-send timers scheduled from the iframe's onLoad (see below) —
  // tracked so a reload/unmount cancels any still-pending resends.
  const retryTimersRef = useRef<number[]>([]);
  useEffect(() => () => retryTimersRef.current.forEach(window.clearTimeout), []);

  // Latest highlight token / result handler, for the same reason as the two refs above:
  // the message listener below is bound once and must not be re-bound per keystroke.
  const highlightTokenRef = useRef(highlightToken);
  highlightTokenRef.current = highlightToken;
  // Same reason as the refs above: the message listener is bound once and must not be
  // re-bound every time the operator picks a different product.
  const productRef = useRef(product);
  productRef.current = product;
  const onHighlightResultRef = useRef(onHighlightResult);
  onHighlightResultRef.current = onHighlightResult;

  // 'studio-preview-ready': the app mounted its listener — (re)send the theme so
  // it isn't lost to the onLoad race that left the preview stuck loading.
  // (Wheel inside the iframe scrolls the certificate; zoom is wheel in the
  // surrounding pane only — handled by the zoomAreaRef listener above.)
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === "studio-preview-ready") {
        sendThemeToIframe(formValuesRef.current, hidePoweredByRef.current, embeddedRef.current);
        // A reload drops the injected highlight stylesheet with the document, so an open
        // panel would silently stop outlining anything. Re-assert it on every handshake.
        sendHighlightToIframe(highlightTokenRef.current);
        sendProductToIframe(productRef.current);
      }
      // VRG-549: the app answering with what it outlined. Ignored unless it is about the
      // token we last asked for — a reply for a stale token would overwrite the current
      // panel's counts with a previous control's.
      if (
        e.data?.type === "studio-highlight-result" &&
        e.data?.token === highlightTokenRef.current
      ) {
        onHighlightResultRef.current?.({
          parts: e.data.parts ?? [],
          invisible: e.data.invisible ?? 0,
        });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <Box
      sx={{
        flex: 1,
        border: `1px solid ${theme.palette.grey[100]}`,
        borderRadius: 3,
        p: 6,
        backgroundColor: theme.palette.grey[50],
        overflowY: "hidden",
        display: "flex",
        flexDirection: "column",
        ...(fillContainer
          ? {
              // The PARENT is the bound. `height: 100%` against a flex parent that has
              // already resolved its own height, plus `minHeight: 0` so this can shrink
              // inside it rather than being floored by its content. No sticky offset and
              // no top margin: both exist to position the pane within a scrolling page,
              // and there is no scrolling page here.
              height: "100%",
              minHeight: 0,
            }
          : {
              minHeight: vhMinus(heightOffset),
              maxHeight: vhMinus(heightOffset),
              position: "sticky",
              top: stickyTop,
              // Rests 16px below the chrome block, and `stickyTop` pins it at the same
              // place — the margin sets RESTING, `top` sets PINNED, and pure-sticky is
              // exactly the two agreeing. See STUDIO_PANE_TOP_GAP.
              mt: `${topGap}px`,
            }),
      }}
    >
      {/* The preview's own control band. `controls` (VRG-470) sits LEFT, the zoom stepper
          stays hard RIGHT at exactly the coordinates it always had — with no `controls`
          passed this renders byte-identically to before, so the certificate drawer and every
          other consumer is untouched.

          `pointerEvents: none` on the band with `auto` on its children, so the band itself
          never swallows a click aimed at the preview behind it. */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{
          position: "absolute",
          top: 12,
          left: 12,
          right: 12,
          zIndex: 2,
          gap: 1,
          pointerEvents: "none",
          "& > *": { pointerEvents: "auto" },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flexShrink: 1 }}>
          {controls}
        </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          flexShrink: 0,
          zIndex: 2,
          bgcolor: "background.paper",
          borderRadius: 2,
          border: `1px solid ${theme.palette.grey[200]}`,
          px: 1,
        }}
      >
        <IconButton size="small" onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))} disabled={zoom <= ZOOM_MIN}>
          <ZoomOut size={16} />
        </IconButton>
        {/*
          TYPEABLE, not a label. The +/- buttons move in 25% steps and the wheel lands on
          arbitrary values, so reaching a specific number (back to 100, straight to 200) was
          a sequence of clicks or an unrepeatable scroll. Typing it is one gesture.

          Hand-rolled on `InputBase` rather than `@repo/ui`'s `Input` deliberately: that
          component's whole job is the field CHROME — the `primary[100]` border, the brand
          focus ring, the `FieldLabel` and the 49px band (working-conventions §31). All of
          that already belongs to the PILL this sits inside, and nesting a second bordered
          chassis in it would draw a box inside a box. `InputBase` is the unstyled
          structural primitive for exactly this case, so the pill keeps owning the chrome.
        */}
        <Stack
          direction="row"
          alignItems="center"
          // Fixed, and wide enough for "200" + "%": the pill must not resize as digits are
          // typed, which would shift the buttons under the cursor mid-edit.
          sx={{ minWidth: 36, justifyContent: "center" }}
        >
          <InputBase
            value={zoomDraft ?? String(zoomPercent)}
            onChange={e => setZoomDraft(e.target.value)}
            onFocus={e => {
              setZoomDraft(String(zoomPercent));
              e.target.select();
            }}
            // Blur commits as well as Enter: clicking away is an ordinary way to finish
            // editing, and a value that silently evaporated on blur would read as a bug.
            // Unless Escape caused this blur, in which case it is a cancel — see the ref.
            onBlur={() => {
              if (zoomEscapedRef.current) {
                zoomEscapedRef.current = false;
                setZoomDraft(null);
                return;
              }
              commitZoomDraft();
            }}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitZoomDraft();
                (e.target as HTMLInputElement).blur();
              } else if (e.key === "Escape") {
                // MUST stop here. This input lives inside a Drawer, and MUI's Modal closes
                // on Escape — without this, abandoning an edit would dismiss the entire
                // preview, which is a far larger undo than the one being asked for.
                e.stopPropagation();
                zoomEscapedRef.current = true;
                setZoomDraft(null);
                (e.target as HTMLInputElement).blur();
              }
            }}
            inputProps={{
              inputMode: "numeric",
              "aria-label": "Zoom percentage",
              // Right-aligned so the number sits flush against the "%" and the pair reads
              // as one value rather than as a field with a unit parked beside it.
              style: { textAlign: "right", padding: 0, height: "auto" },
            }}
            sx={{ typography: "body3", color: "inherit", width: 26 }}
          />
          {/* Outside the input, so it can never be selected, deleted or parsed. */}
          <Typography variant="body3" component="span">
            %
          </Typography>
        </Stack>
        <IconButton size="small" onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))} disabled={zoom >= ZOOM_MAX}>
          <ZoomIn size={16} />
        </IconButton>
      </Stack>
      </Stack>

      <Stack
        ref={zoomAreaRef}
        direction="column"
        alignItems="center"
        sx={{
          flex: 1,
          py: 4,
          overflow: "auto",
          width: "100%",
          /**
           * `safe center`, NOT plain `center`.
           *
           * Centred flex content that outgrows its scroller overflows in BOTH directions, and
           * the overflow above the start edge is unreachable — `scrollTop` is already 0 there,
           * so there is nothing left to scroll. Measured at a 640px viewport with the drawer
           * opening at 150%: the phone's top sat at -64.5px and stayed there at `scrollTop: 0`,
           * i.e. the first 64px of the certificate could not be seen by any means.
           *
           * `safe` says "centre me until that would lose content, then align to start", which
           * is the behaviour this always wanted: centred when it fits, scrollable when it does
           * not. Applies to every container — Studio's deep zooms overflow the same way.
           */
          justifyContent: "safe center",
        }}
      >
        {/*
          The install location sits ABOVE the device because that is where an address
          lives on a real phone — it reads as the address bar, which is exactly what it
          is saying: this is where the page is served from. The "<Device> preview" caption
          stays BELOW, because it describes the picture rather than the page in it.
        */}
        {installOptions && installOptions.length > 1 && (
          <Box sx={{ mb: 3 }}>
            <ButtonBase
              onClick={e => setInstallAnchor(e.currentTarget)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                borderRadius: 1,
                px: 1,
                py: 0.5,
                color: theme.palette.text.secondary,
                "&:hover": { color: theme.palette.text.primary },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.brand[600]}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Typography variant="body3" color="inherit">
                {installLabel}
              </Typography>
              <ChevronDown size={14} strokeWidth={1.5} />
            </ButtonBase>
            <Menu
              anchorEl={installAnchor}
              open={!!installAnchor}
              onClose={() => setInstallAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
              slotProps={{ paper: { sx: { ...MENU_PAPER_SX, ...menuPaperChromeSx(theme) } } }}
            >
              {installOptions.map(opt => (
                <MenuItem
                  key={opt.label}
                  selected={opt.label === installLabel}
                  onClick={() => {
                    onInstallChange?.(opt.label);
                    setInstallAnchor(null);
                  }}
                  sx={{ gap: 2, justifyContent: "space-between" }}
                >
                  <Typography variant="body3">{opt.label}</Typography>
                  {opt.label === installLabel && <Check size={14} strokeWidth={1.5} />}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
        <Box
          sx={{
            // Slim device bezel: a thin dark frame around the screen + a soft drop
            // shadow so the preview reads as a real phone/monitor and lifts off the
            // preview surface. Mobile gets a rounder frame; desktop is tighter
            // (monitor-like).
            //
            // The weight is mode-dependent. On a DARK surface the near-black bezel
            // nearly disappears into the background, so it needs thickness and a
            // heavy shadow to stay legible. On a LIGHT surface that same frame is
            // full black-on-white contrast and reads as a chunky slab, so it wants
            // much less: a hairline frame and a softer shadow.
            borderRadius: device === "mobile" ? 6 : 3.5,
            p: theme.palette.mode === "light"
              ? (device === "mobile" ? 0.75 : 1)
              : (device === "mobile" ? 3 : 2),
            backgroundColor: "#111113",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: theme.palette.mode === "light"
              ? "0 10px 28px -8px rgba(0,0,0,0.35)"
              : "0 18px 52px -10px rgba(0,0,0,0.65)",
          }}
        >
          <Box
            sx={{
              width: framedWidth,
              height: framedHeight,
              overflow: "hidden",
              position: "relative",
              borderRadius: device === "mobile" ? 4.5 : 2,
              backgroundColor: "#fff",
              /*
                The iframe carries `transform: scale(...)`, and a TRANSFORMED child is not
                clipped by an ancestor's `border-radius` — `overflow: hidden` alone leaves
                the page's square corners poking out over the bezel's rounded ones, which
                is exactly what you see as four white notches.
                `isolation` + a null 3D transform promote this box to its own compositing
                layer, which is what makes the rounded clip apply to the scaled content;
                the `-webkit-mask-image` is the long-standing Safari equivalent, harmless
                elsewhere. All three are needed for the fix to hold across engines.
              */
              isolation: "isolate",
              transform: "translateZ(0)",
              WebkitMaskImage: "-webkit-radial-gradient(white, black)",
            }}
          >
          {/*
            No loader overlay. The previewed app renders its OWN loading state inside this
            frame — a skeleton, per §19 — and a VERA spinner on top of it hid that and
            showed the brand something the real page never does. The frame sits empty for
            the moment before the iframe paints, which is honest: that IS what an empty
            device looks like. `isLoadingIframe` is still tracked (onLoad clears it) so the
            state is available if a future container needs it.
          */}
          <iframe
            title="Studio preview"
            style={{
              width: frame.viewportWidth,
              height: frame.viewportHeight,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              border: 0,
              maxWidth: "unset",
              display: "block",
            }}
            src={`${previewUrl}/?isPreview=true&interactive=true&page=${previewPage}${
              certificateId ? `&certificate_id=${encodeURIComponent(certificateId)}` : ""
            }${companyId ? `&company_id=${encodeURIComponent(companyId)}` : ""}`}
            ref={iframeRef}
            onLoad={() => {
              // The iframe's document firing `load` does NOT mean its React app has
              // mounted its own `message` listener yet, so this single post can land
              // in the gap and be dropped -- leaving the preview blank forever with
              // no loader to explain it. The "studio-preview-ready" handshake above
              // is meant to cover that, but isn't reliable in dev (StrictMode's
              // double-effect remount can re-register the listener after the reply).
              // Re-send a few times over the next couple of seconds; each send is
              // idempotent (the iframe just re-applies the same theme), and the
              // timers are cancelled on unmount/reload.
              sendThemeToIframe(formValuesRef.current, hidePoweredByRef.current, embeddedRef.current);
              sendHighlightToIframe(highlightTokenRef.current);
              sendProductToIframe(productRef.current);
              setIsLoadingIframe(false);
              retryTimersRef.current.forEach(window.clearTimeout);
              retryTimersRef.current = [150, 400, 1000, 2000].map((delay) =>
                window.setTimeout(
                  () => {
                    sendThemeToIframe(formValuesRef.current, hidePoweredByRef.current, embeddedRef.current);
                    sendHighlightToIframe(highlightTokenRef.current);
                    sendProductToIframe(productRef.current);
                  },
                  delay
                )
              );
            }}
          />
          </Box>
        </Box>
        <Typography variant="body3" color="text.secondary" sx={{ mt: 4 }}>
          {frame.label} preview
        </Typography>
      </Stack>
    </Box>
  );
};
